import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { del, get, patch, post } from "../../lib/request"

// GET content pillars by client ID
export const fetchContentPillarsByClientId = createAsyncThunk(
  "contentPillars/fetchByClientId",
  async ({ clientId }, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const token = state.auth?.token

      if (!token) throw new Error("Authentication required")
      if (!clientId) throw new Error("Client ID is required")

      const data = await get(`client-content-pillars/clients/${clientId}/pillars`)
      return data

    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch content pillars")
    }
  },
  {
    condition: ({ clientId, force } = {}, { getState }) => {
      if (force) return true
      if (!clientId) return false
      const slice = getState().contentPillars ?? {}
      const same = String(slice.fetchedForClientId ?? "") === String(clientId)
      if (slice.status === "loading" && same) return false
      if (slice.status === "succeeded" && same) return false
      return true
    },
  },
)


// POST: update target for a content pillar
export const updateContentPillarTarget = createAsyncThunk(
  "contentPillars/updateContentPillarTarget",
  async ({ clientId, clientPillarId, contentPillarId, target }, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const token = state.auth?.token
      if (!token) throw new Error("Authentication required")

      if (!clientId || target === undefined) {
        throw new Error("clientId and target are required")
      }

      if (clientPillarId != null && String(clientPillarId).trim() !== "") {
        const url = `client-content-pillars/${encodeURIComponent(String(clientPillarId))}?target=${encodeURIComponent(String(target))}`
        const response = await patch(url, undefined)
        return { clientPillarId, contentPillarId, target, response }
      }

      if (contentPillarId == null || String(contentPillarId).trim() === "") {
        throw new Error("contentPillarId is required when clientPillarId is missing")
      }
      const url = `client-content-pillars/clients/${encodeURIComponent(String(clientId))}/pillars?content_pillar_id=${encodeURIComponent(String(contentPillarId))}&target=${encodeURIComponent(String(target))}`
      const response = await post(url, undefined)
      const createdClientPillarId =
        response?.id ??
        response?.clientPillarId ??
        (response?.data && typeof response.data === "object" ? response.data.id : null) ??
        null
      return { clientPillarId: createdClientPillarId, contentPillarId, target, response }

    } catch (err) {
      return rejectWithValue(err?.message || "Failed to update content pillar target")
    }
  }
)

export const createContentPillarForClient = createAsyncThunk(
  "contentPillars/createForClient",
  async ({ clientId, name, description, target }, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      if (!state.auth?.token) throw new Error("Authentication required")
      if (!clientId || !String(name || "").trim()) {
        throw new Error("Client ID and pillar name are required")
      }
      const t = typeof target === "number" && !Number.isNaN(target) ? target : Number.parseInt(target, 10) || 0
      const body = {
        name: String(name).trim(),
        description: String(description || name).trim(),
        target: Math.max(0, Math.min(20, t)),
      }
      const data = await post(`content-pillars/clients/${clientId}/pillars`, body)
      return data
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to create content pillar")
    }
  }
)

export const deleteContentPillarForClient = createAsyncThunk(
  "contentPillars/deleteForClient",
  async ({ clientPillarId, contentPillarId }, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      if (!state.auth?.token) throw new Error("Authentication required")
      if (clientPillarId == null || clientPillarId === "") {
        throw new Error("Client pillar ID is required")
      }
      const cpId = encodeURIComponent(String(clientPillarId))
      await del(`client-content-pillars/${cpId}`)
      if (contentPillarId != null && contentPillarId !== "") {
        const pId = encodeURIComponent(String(contentPillarId))
        await del(`content-pillars/${pId}/client-pillar/${cpId}`)
      }
      return { clientPillarId, contentPillarId }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to remove content pillar")
    }
  }
)


const initialState = {
  pillars: [],
  status: "idle",
  error: null,
  fetchedForClientId: null,
}

const contentPillarsSlice = createSlice({
  name: "contentPillars",
  initialState,
  reducers: {
    clearContentPillars: (state) => {
      state.pillars = []
      state.status = "idle"
      state.error = null
      state.fetchedForClientId = null
    },
    clearContentPillarsError: (state) => {
      state.error = null
    },
    resetContentPillarsOnLogout: (state) => {
      state.pillars = []
      state.status = "idle"
      state.error = null
      state.fetchedForClientId = null
    },
  },

  extraReducers: (builder) => {
    builder

      // GET pillars
      .addCase(fetchContentPillarsByClientId.pending, (state, action) => {
        state.status = "loading"
        state.error = null
        const nextId = action.meta?.arg?.clientId
        if (nextId != null) state.fetchedForClientId = nextId
      })
      .addCase(fetchContentPillarsByClientId.fulfilled, (state, action) => {
        state.status = "succeeded"
        state.pillars = action.payload
        const nextId = action.meta?.arg?.clientId
        if (nextId != null) state.fetchedForClientId = nextId
      })
      .addCase(fetchContentPillarsByClientId.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload
      })

      // POST update pillar target
      .addCase(updateContentPillarTarget.pending, (state) => {
        state.status = "loading"
      })
      .addCase(updateContentPillarTarget.fulfilled, (state, action) => {
        state.status = "succeeded"

        const { clientPillarId, contentPillarId, target } = action.payload
        const index = state.pillars.findIndex((p) => {
          if (clientPillarId != null && String(clientPillarId).trim() !== "") {
            return String(p?.id) === String(clientPillarId)
          }
          return String(p?.contentPillar?.id) === String(contentPillarId)
        })
        if (index !== -1) {
          state.pillars[index].target = target
          if (
            (state.pillars[index].id == null || state.pillars[index].id === "") &&
            clientPillarId != null &&
            String(clientPillarId).trim() !== ""
          ) {
            state.pillars[index].id = clientPillarId
          }
        }
      })
      .addCase(updateContentPillarTarget.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload
      })

      .addCase(createContentPillarForClient.pending, (state) => {
        state.status = "loading"
        state.error = null
      })
      .addCase(createContentPillarForClient.fulfilled, (state) => {
        state.status = "succeeded"
      })
      .addCase(createContentPillarForClient.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload
      })

      .addCase(deleteContentPillarForClient.pending, (state) => {
        state.status = "loading"
        state.error = null
      })
      .addCase(deleteContentPillarForClient.fulfilled, (state, action) => {
        state.status = "succeeded"
        const removeId = action.payload?.contentPillarId
        const removeClientPillarId = action.payload?.clientPillarId
        if (removeId != null) {
          state.pillars = (state.pillars || []).filter(
            (p) => String(p?.contentPillar?.id) !== String(removeId),
          )
          return
        }
        if (removeClientPillarId != null) {
          state.pillars = (state.pillars || []).filter(
            (p) => String(p?.id) !== String(removeClientPillarId),
          )
        }
      })
      .addCase(deleteContentPillarForClient.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload
      })
  },
})

export const {
  clearContentPillars,
  clearContentPillarsError,
  resetContentPillarsOnLogout
} = contentPillarsSlice.actions

export default contentPillarsSlice.reducer
